"use client";

import { useState, useRef } from "react";
import { Camera, Loader2, Upload, X } from "lucide-react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import type { ProfileData } from "../types";
interface ChangeAvatarTabProps {
  profileData: ProfileData;
  onAvatarChanged: () => Promise<void>;
  labels: Record<string, string>;
}
export default function ChangeAvatarTab({
  profileData,
  onAvatarChanged,
  labels
}: ChangeAvatarTabProps) {
  const sdk = useFansUnitedSDK();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const currentAvatar = profileData.avatar;
  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(labels.avatarInvalidType || "Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(labels.avatarTooLarge || "Image must be smaller than 5MB");
      return;
    }
    setError(null);
    setSuccess(false);
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };
  const handleClearSelection = () => {
    setPreview(null);
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const handleUpload = async () => {
    if (!selectedFile || !sdk) return;
    setIsUploading(true);
    setError(null);
    setSuccess(false);
    try {
      // Upload to Payload
      const formData = new FormData();
      formData.append("file", selectedFile);
      console.log("[ChangeAvatar] Uploading file:", selectedFile.name, selectedFile.type, selectedFile.size);
      const response = await fetch("/api/avatar", {
        method: "POST",
        body: formData
      });
      console.log("[ChangeAvatar] Upload response status:", response.status);
      const responseData = await response.json();
      console.log("[ChangeAvatar] Upload response data:", responseData);
      if (!response.ok) {
        throw new Error(responseData.error || "Upload failed");
      }
      const {
        url
      } = responseData;
      console.log("[ChangeAvatar] Got URL, updating SDK profile:", url);

      // Update SDK profile with new avatar URL
      try {
        await sdk.profile.getOwn().setAvatar(url).update();
        console.log("[ChangeAvatar] SDK profile updated successfully");
      } catch (sdkErr) {
        // SDK may reject localhost URLs — log but don't block the flow
        console.warn("[ChangeAvatar] SDK avatar update failed (may work in production):", sdkErr);
      }
      await onAvatarChanged();
      setSuccess(true);
      setSelectedFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("[ChangeAvatar] Error:", err);
      setError(err instanceof Error ? err.message : labels.avatarUploadError || "Failed to upload avatar");
    } finally {
      setIsUploading(false);
    }
  };
  const displayImage = preview || currentAvatar;
  return <div className="space-y-6">
            {/* Current Avatar Display */}
            <div className="flex flex-col items-center gap-4">
                <div className="relative">
                    {displayImage ? <img src={displayImage} alt={profileData.name} className="h-32 w-32 rounded-full object-cover border-2 border-border" /> : <div className="h-32 w-32 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-3xl font-medium border-2 border-border">
                            {getInitials(profileData.name || "U")}
                        </div>}
                    {preview && <button onClick={handleClearSelection} className="absolute -top-1 -right-1 rounded-full bg-error p-1 text-white hover:bg-error/80 transition cursor-pointer">
                            <X className="h-3.5 w-3.5" />
                        </button>}
                </div>

                <p className="text-sm text-muted-foreground">
                    {labels.avatarHint || "Recommended: Square image, at least 200x200px"}
                </p>
            </div>

            {/* Messages */}
            {success && <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-600">
                    {labels.avatarUpdated || "Avatar updated successfully"}
                </div>}
            {error && <div className="rounded-lg bg-error/10 border border-error/20 p-3 text-sm text-error">{error}</div>}

            {/* File Input & Actions */}
            <div className="flex flex-col items-center gap-3">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

                {!selectedFile ? <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-dashed border-border px-6 py-3 text-sm font-medium text-foreground hover:bg-muted transition cursor-pointer">
                        <Camera className="h-4 w-4" />
                        {labels.selectImage || "Select Image"}
                    </button> : <div className="flex gap-3">
                        <button onClick={handleUpload} disabled={isUploading} className="flex items-center gap-2 btn-primary px-4 py-2 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            {isUploading ? labels.uploading || "Uploading..." : labels.uploadAvatar || "Upload"}
                        </button>
                        <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                            {labels.chooseAnother || "Choose Another"}
                        </button>
                    </div>}
            </div>
        </div>;
}